import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { Button } from '@/components/ui/button';
import { getBooking } from '@/lib/api';
import { toast } from 'sonner';
import { CheckCircle, Calendar, Clock, User, Car, Phone, Mail, ArrowLeft, Settings, CalendarPlus, Share2, Copy, MessageCircle, Hourglass, XCircle } from 'lucide-react';

export default function BookingConfirmation() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBooking();
  }, [bookingId]);

  const fetchBooking = async () => {
    try {
      const response = await getBooking(bookingId);
      setBooking(response.data);
    } catch (err) {
      setError('Booking not found');
    } finally {
      setLoading(false);
    }
  };

  // Parse a slot label like "6:30 PM - 9:30 PM" or "8:30am-12:30pm" into 24h {startH,startM,endH,endM}
  const parseSlot = (label) => {
    if (!label) return null;
    const norm = label.toLowerCase().replace(/\s+/g, '');
    const parts = norm.split('-');
    if (parts.length !== 2) return null;
    const parseTime = (t) => {
      const m = t.match(/(\d{1,2}):(\d{2})(am|pm)?/);
      if (!m) return null;
      let h = parseInt(m[1], 10);
      const min = parseInt(m[2], 10);
      const ap = m[3];
      if (ap === 'pm' && h < 12) h += 12;
      if (ap === 'am' && h === 12) h = 0;
      return { h, m: min };
    };
    const start = parseTime(parts[0]);
    const end = parseTime(parts[1]);
    if (!start || !end) return null;
    return { startH: start.h, startM: start.m, endH: end.h, endM: end.m };
  };

  const pad = (n) => String(n).padStart(2, '0');

  const buildIcs = () => {
    if (!booking) return null;
    const times = parseSlot(booking.slot_label);
    if (!times) return null;
    const d = booking.date; // YYYY-MM-DD
    const dateStr = d.replace(/-/g, '');
    const dtStart = `${dateStr}T${pad(times.startH)}${pad(times.startM)}00`;
    const dtEnd = `${dateStr}T${pad(times.endH)}${pad(times.endM)}00`;
    const now = new Date();
    const dtStamp = `${now.getUTCFullYear()}${pad(now.getUTCMonth() + 1)}${pad(
      now.getUTCDate()
    )}T${pad(now.getUTCHours())}${pad(now.getUTCMinutes())}${pad(now.getUTCSeconds())}Z`;
    const vehicle = `${booking.customer.vehicle_year || ''} ${booking.customer.vehicle_make || ''} ${booking.customer.vehicle_model || ''}`.trim();
    const description = `Dyno session with NAKI TUNE 2.0.\\nVehicle: ${vehicle}${booking.customer.vehicle_registration ? `\\nRego: ${booking.customer.vehicle_registration}` : ''}${booking.customer.notes ? `\\nNotes: ${booking.customer.notes}` : ''}\\nContact: William 0278150446`;
    const ics = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//NAKI TUNE 2.0//Booking//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:${booking.id}@nakitune.co.nz`,
      `DTSTAMP:${dtStamp}`,
      `DTSTART;TZID=Pacific/Auckland:${dtStart}`,
      `DTEND;TZID=Pacific/Auckland:${dtEnd}`,
      'SUMMARY:NAKI TUNE 2.0 - Dyno Session',
      `DESCRIPTION:${description}`,
      'LOCATION:NAKI TUNE 2.0 Workshop',
      'STATUS:CONFIRMED',
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\r\n');
    return ics;
  };

  const handleAddToCalendar = () => {
    const ics = buildIcs();
    if (!ics) return;
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `naki-tune-booking-${booking.id.slice(0, 8)}.ics`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const buildShareText = () => {
    if (!booking) return '';
    const dateStr = format(parseISO(booking.date + 'T00:00:00'), 'EEE, MMM d, yyyy');
    const vehicle = `${booking.customer.vehicle_year || ''} ${booking.customer.vehicle_make || ''} ${booking.customer.vehicle_model || ''}`.trim();
    return `NAKI TUNE 2.0 dyno booking confirmed!\n\nDate: ${dateStr}\nTime: ${booking.slot_label}\nVehicle: ${vehicle}\n\nView details: ${window.location.href}`;
  };

  const handleNativeShare = async () => {
    const text = buildShareText();
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'NAKI TUNE 2.0 Booking',
          text,
          url: window.location.href,
        });
      } catch (e) {
        // user cancelled
      }
    } else {
      handleCopyLink();
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Booking link copied to clipboard!');
    } catch (e) {
      toast.error('Could not copy link');
    }
  };

  const handleWhatsAppShare = () => {
    const text = encodeURIComponent(buildShareText());
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const handleEmailShare = () => {
    const subject = encodeURIComponent('NAKI TUNE 2.0 dyno booking');
    const body = encodeURIComponent(buildShareText());
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-zinc-500">Loading booking...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <Button onClick={() => navigate('/')} variant="outline">
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 relative overflow-hidden">
      {/* Ghost Text Background */}
      <div className="ghost-text">NAKI TUNE 2.0</div>

      {/* Header */}
      <header className="relative z-10 border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-lg">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-600 flex items-center justify-center">
              <Settings className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="brand-title text-2xl uppercase tracking-wide text-white">
                NAKI TUNE 2.0
              </h1>
              <p className="text-xs text-zinc-500 uppercase tracking-[0.15em]">Dyno Tuning</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 container mx-auto px-4 py-12 max-w-2xl">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8"
          data-testid="back-to-booking"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm uppercase tracking-wider">Book Another Session</span>
        </button>

        <div className="glass p-8 border border-zinc-800 bg-zinc-900/50 animate-fade-in-up">
          {/* Status Header */}
          {(() => {
            const status = booking.status || 'pending';
            if (status === 'pending') {
              return (
                <div className="text-center mb-8" data-testid="status-pending">
                  <div className="w-16 h-16 bg-yellow-900/30 border border-yellow-800 rounded-sm flex items-center justify-center mx-auto mb-4">
                    <Hourglass className="w-8 h-8 text-yellow-500" />
                  </div>
                  <h2 className="font-['Barlow_Condensed'] text-3xl font-black uppercase tracking-tight text-white italic mb-2">
                    Awaiting Approval
                  </h2>
                  <p className="text-zinc-400">
                    Thanks — William has been notified and will review your request shortly. You&apos;ll get an email once he approves it.
                  </p>
                </div>
              );
            }
            if (status === 'denied') {
              return (
                <div className="text-center mb-8" data-testid="status-denied">
                  <div className="w-16 h-16 bg-red-900/30 border border-red-800 rounded-sm flex items-center justify-center mx-auto mb-4">
                    <XCircle className="w-8 h-8 text-red-500" />
                  </div>
                  <h2 className="font-['Barlow_Condensed'] text-3xl font-black uppercase tracking-tight text-white italic mb-2">
                    Booking Not Accepted
                  </h2>
                  <p className="text-zinc-400">
                    Unfortunately we cannot fit you in for this slot.
                    {booking.denial_reason ? ` Reason: ${booking.denial_reason}.` : ''} Please reach out to nakitune@outlook.co.nz to find another time.
                  </p>
                </div>
              );
            }
            if (status === 'cancelled') {
              return (
                <div className="text-center mb-8" data-testid="status-cancelled">
                  <div className="w-16 h-16 bg-zinc-900/50 border border-zinc-700 rounded-sm flex items-center justify-center mx-auto mb-4">
                    <XCircle className="w-8 h-8 text-zinc-400" />
                  </div>
                  <h2 className="font-['Barlow_Condensed'] text-3xl font-black uppercase tracking-tight text-white italic mb-2">
                    Booking Cancelled
                  </h2>
                  <p className="text-zinc-400">This booking has been cancelled.</p>
                </div>
              );
            }
            return (
              <div className="text-center mb-8" data-testid="status-confirmed">
                <div className="w-16 h-16 bg-green-900/30 border border-green-800 rounded-sm flex items-center justify-center mx-auto mb-4 pulse-green">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <h2 className="font-['Barlow_Condensed'] text-3xl font-black uppercase tracking-tight text-white italic mb-2">
                  Booking Confirmed!
                </h2>
                <p className="text-zinc-400">
                  Your dyno session has been scheduled. We&apos;ll see you soon!
                </p>
              </div>
            );
          })()}

          {/* Booking Details */}
          <div className="space-y-6">
            {/* Date & Time */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-zinc-900 p-4 border border-zinc-800">
                <div className="flex items-center gap-2 text-zinc-500 mb-2">
                  <Calendar className="w-4 h-4" />
                  <span className="text-xs uppercase tracking-[0.15em]">Date</span>
                </div>
                <p className="font-['JetBrains_Mono'] text-white text-lg">
                  {format(parseISO(booking.date + 'T00:00:00'), 'EEE, MMM d, yyyy')}
                </p>
              </div>
              <div className="bg-zinc-900 p-4 border border-zinc-800">
                <div className="flex items-center gap-2 text-zinc-500 mb-2">
                  <Clock className="w-4 h-4" />
                  <span className="text-xs uppercase tracking-[0.15em]">Time</span>
                </div>
                <p className="font-['JetBrains_Mono'] text-white text-lg">
                  {booking.slot_label}
                </p>
              </div>
            </div>

            {/* Customer Info */}
            <div className="border-t border-zinc-800 pt-6">
              <div className="flex items-center gap-2 text-zinc-500 mb-4">
                <User className="w-4 h-4" />
                <span className="text-xs uppercase tracking-[0.15em]">Your Details</span>
              </div>
              <div className="space-y-2">
                <p className="text-white font-medium">{booking.customer.name}</p>
                <p className="text-zinc-400 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  {booking.customer.phone}
                </p>
                <p className="text-zinc-400 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {booking.customer.email}
                </p>
              </div>
            </div>

            {/* Vehicle Info */}
            <div className="border-t border-zinc-800 pt-6">
              <div className="flex items-center gap-2 text-zinc-500 mb-4">
                <Car className="w-4 h-4" />
                <span className="text-xs uppercase tracking-[0.15em]">Vehicle</span>
              </div>
              <p className="text-white">
                {booking.customer.vehicle_year} {booking.customer.vehicle_make} {booking.customer.vehicle_model}
              </p>
              {booking.customer.vehicle_registration && (
                <p className="text-zinc-500 text-sm mt-1">
                  Rego: {booking.customer.vehicle_registration}
                </p>
              )}
              {booking.customer.notes && (
                <p className="text-zinc-500 text-sm mt-2 italic">
                  Notes: "{booking.customer.notes}"
                </p>
              )}
            </div>

            {/* Booking Reference */}
            <div className="border-t border-zinc-800 pt-6">
              <p className="text-xs text-zinc-500 uppercase tracking-[0.15em] mb-2">
                Booking Reference
              </p>
              <p className="font-['JetBrains_Mono'] text-green-500 text-sm break-all">
                {booking.id}
              </p>
            </div>

            {/* Add to Calendar (only for confirmed bookings) */}
            {booking.status === 'confirmed' && (
              <div className="border-t border-zinc-800 pt-6">
                <Button
                  onClick={handleAddToCalendar}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-['Barlow_Condensed'] uppercase tracking-wider text-sm py-6"
                  data-testid="add-to-calendar-btn"
                >
                  <CalendarPlus className="w-4 h-4 mr-2" />
                  Add to Calendar
                </Button>
                <p className="text-xs text-zinc-500 mt-2 text-center">
                  Downloads a .ics file that works with Google, Apple, Outlook & more
                </p>
              </div>
            )}

            {/* Share Booking (only for confirmed) */}
            {booking.status === 'confirmed' && (
            <div className="border-t border-zinc-800 pt-6">
              <p className="text-xs text-zinc-500 uppercase tracking-[0.15em] mb-3 flex items-center gap-2">
                <Share2 className="w-4 h-4" />
                Share Booking
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Button
                  onClick={handleNativeShare}
                  variant="outline"
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white"
                  data-testid="share-native-btn"
                >
                  <Share2 className="w-4 h-4 mr-1" /> Share
                </Button>
                <Button
                  onClick={handleWhatsAppShare}
                  variant="outline"
                  className="border-zinc-700 text-zinc-300 hover:bg-green-900/30 hover:text-green-400 hover:border-green-700"
                  data-testid="share-whatsapp-btn"
                >
                  <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
                </Button>
                <Button
                  onClick={handleEmailShare}
                  variant="outline"
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white"
                  data-testid="share-email-btn"
                >
                  <Mail className="w-4 h-4 mr-1" /> Email
                </Button>
                <Button
                  onClick={handleCopyLink}
                  variant="outline"
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white"
                  data-testid="share-copy-btn"
                >
                  <Copy className="w-4 h-4 mr-1" /> Copy
                </Button>
              </div>
            </div>
            )}
          </div>

          {/* Contact Info */}
          <div className="mt-8 pt-6 border-t border-zinc-800 text-center">
            <p className="text-zinc-500 text-sm mb-4">
              Need to cancel or reschedule? Contact us:
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:0278150446"
                className="flex items-center gap-2 text-white hover:text-green-500 transition-colors"
              >
                <Phone className="w-4 h-4" />
                <span className="font-['JetBrains_Mono']">0278150446</span>
              </a>
              <a
                href="mailto:Nakitune@outlook.co.nz"
                className="flex items-center gap-2 text-white hover:text-green-500 transition-colors"
              >
                <Mail className="w-4 h-4" />
                <span className="font-['JetBrains_Mono']">Nakitune@outlook.co.nz</span>
              </a>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
